import { MINDMAP_DATA_KEY } from "./types";

import type { ExcalidrawElement } from "@excalidraw/element/types";

import type { MindmapNodeData, PlusDirection } from "./types";

export type MindmapNodeElement = ExcalidrawElement & {
  type: "rectangle";
  customData: { [MINDMAP_DATA_KEY]: MindmapNodeData };
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const isMindmapNodeData = (value: unknown): value is MindmapNodeData =>
  isRecord(value) &&
  value.__drawolaKind === "mindmapNode" &&
  typeof value.mindmapId === "string" &&
  typeof value.nodeId === "string";

export function isMindmapNode<E extends ExcalidrawElement>(
  el: E,
): el is E & MindmapNodeElement {
  if (el.type !== "rectangle" || el.isDeleted) {
    return false;
  }
  const data = (el.customData as Record<string, unknown> | undefined)?.[
    MINDMAP_DATA_KEY
  ];
  return isMindmapNodeData(data);
}

// Retrieve the structured mindmap payload from a node already verified by
// isMindmapNode(). Throws in dev if invariants are broken.
export const getNodeData = (el: MindmapNodeElement): MindmapNodeData => {
  const data = (el.customData as Record<string, unknown> | undefined)?.[
    MINDMAP_DATA_KEY
  ];
  return data as MindmapNodeData;
};

export const getMindmapData = (
  el: ExcalidrawElement,
): MindmapNodeData | null => {
  if (!isMindmapNode(el)) {
    return null;
  }
  return getNodeData(el);
};

export const getMindmapNodes = (
  elements: readonly ExcalidrawElement[],
): MindmapNodeElement[] =>
  elements.filter((el): el is MindmapNodeElement => isMindmapNode(el));

export const findMindmapNodeByNodeId = (
  elements: readonly ExcalidrawElement[],
  nodeId: string,
): MindmapNodeElement | null => {
  for (const el of elements) {
    if (isMindmapNode(el) && el.customData[MINDMAP_DATA_KEY].nodeId === nodeId) {
      return el;
    }
  }
  return null;
};

export const findParent = (
  elements: readonly ExcalidrawElement[],
  node: MindmapNodeElement,
): MindmapNodeElement | null => {
  const data = node.customData[MINDMAP_DATA_KEY];
  if (!data.parentId) {
    return null;
  }
  return findMindmapNodeByNodeId(elements, data.parentId);
};

export const findChildren = (
  elements: readonly ExcalidrawElement[],
  parent: MindmapNodeElement,
): MindmapNodeElement[] => {
  const parentNodeId = parent.customData[MINDMAP_DATA_KEY].nodeId;
  return getMindmapNodes(elements).filter(
    (el) => el.customData[MINDMAP_DATA_KEY].parentId === parentNodeId,
  );
};

export const findChildrenOnSide = (
  elements: readonly ExcalidrawElement[],
  parent: MindmapNodeElement,
  side: "L" | "R",
): MindmapNodeElement[] =>
  findChildren(elements, parent).filter(
    (el) => el.customData[MINDMAP_DATA_KEY].side === side,
  );

export const findSiblings = (
  elements: readonly ExcalidrawElement[],
  node: MindmapNodeElement,
): MindmapNodeElement[] => {
  const data = node.customData[MINDMAP_DATA_KEY];
  if (!data.parentId) {
    return [];
  }
  return getMindmapNodes(elements).filter((el) => {
    const peerData = el.customData[MINDMAP_DATA_KEY];
    return (
      peerData.parentId === data.parentId && peerData.nodeId !== data.nodeId
    );
  });
};

// Sides where a + icon should appear on a given node.
//
// Rules (per spec):
// - root (parentId === null): only L and R, regardless of existing children
//   (each click adds another root child on that side).
// - non-root with side === "R": parent sits to the LEFT, so hide +L.
//   Show +T, +B (sibling), +R (further child).
// - non-root with side === "L": parent sits to the RIGHT, so hide +R.
//   Show +T, +B (sibling), +L (further child).
export const getAllowedPlusDirections = (
  node: MindmapNodeElement,
): PlusDirection[] => {
  const { parentId, side } = node.customData[MINDMAP_DATA_KEY];
  if (parentId === null) {
    return ["L", "R"];
  }
  if (side === "R") {
    return ["T", "B", "R"];
  }
  if (side === "L") {
    return ["T", "B", "L"];
  }
  // Defensive fallback (should not happen for a well-formed tree).
  return ["T", "B"];
};
